function parseLocalDateIso(dateStr) {
    const dateOnly = dateStr.substring(0, 10);
    const parsed = new Date(`${dateOnly}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}
/**
 * @function resolvePlannedDateForUpdate
 * @since 1.0.0
 * @description Returns the plannedDate string to send on updateDeal.
 * Empty string clears the date (JSON keeps "", unlike null/undefined).
 * `undefined` omits the field so the backend keeps the stored dates.
 */
export function resolvePlannedDateForUpdate(deal) {
    if (deal.plannedDate !== undefined) {
        return deal.plannedDate || '';
    }
    if (!deal.plannedDateStart) {
        return '';
    }
    return undefined;
}
/**
 * @function parsePlannedDateToRange
 * @since 1.0.0
 */
export function parsePlannedDateToRange(plannedDate, mode) {
    if (!plannedDate) {
        return { plannedDateEnd: null, plannedDateStart: null };
    }
    if (mode === 'DATE') {
        return {
            plannedDateEnd: null,
            plannedDateStart: parseLocalDateIso(plannedDate)
        };
    }
    if (mode === 'RANGE') {
        const separator = plannedDate.includes(' - ') ? ' - ' : ',';
        const parts = plannedDate.split(separator).map((part) => part.trim());
        if (parts.length === 2) {
            return {
                plannedDateEnd: parseLocalDateIso(parts[1]),
                plannedDateStart: parseLocalDateIso(parts[0])
            };
        }
        if (parts.length === 1) {
            const start = parseLocalDateIso(parts[0]);
            return { plannedDateEnd: start, plannedDateStart: start };
        }
    }
    return {
        plannedDateEnd: null,
        plannedDateStart: parseLocalDateIso(plannedDate)
    };
}
/**
 * @function resolvePlannedDateRange
 * @since 1.0.0
 */
export function resolvePlannedDateRange(deal, mode = 'DATE') {
    var _a, _b;
    if (deal.plannedDateStart) {
        return {
            plannedDateEnd: (_a = deal.plannedDateEnd) !== null && _a !== void 0 ? _a : null,
            plannedDateStart: (_b = deal.plannedDateStart) !== null && _b !== void 0 ? _b : null
        };
    }
    return parsePlannedDateToRange(deal.plannedDate, mode);
}
function parseUtcCalendarDate(value) {
    const dateStr = value.substring(0, 10);
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}
/**
 * @function getPlannedDateDayCount
 * @since 1.0.0
 */
export function getPlannedDateDayCount(deal, plannedDateMode = 'DATE') {
    const { plannedDateStart: start, plannedDateEnd: end } = resolvePlannedDateRange(deal, plannedDateMode);
    if (!start) {
        return 1;
    }
    if (!end) {
        return 1;
    }
    const startDate = parseUtcCalendarDate(String(start));
    const endDate = parseUtcCalendarDate(String(end));
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
}
/**
 * @function getEffectiveConfigurationQuantity
 * @since 1.0.0
 */
export function getEffectiveConfigurationQuantity(storedQuantity, quantityCalculation, dayCount) {
    if (quantityCalculation === 'PER_UNIT_PER_DAY') {
        return storedQuantity * dayCount;
    }
    return storedQuantity;
}
/**
 * @function isPerUnitPerDayCalculation
 * @since 1.0.0
 */
export function isPerUnitPerDayCalculation(quantityCalculation) {
    return quantityCalculation === 'PER_UNIT_PER_DAY';
}
/**
 * @function getConfigurationEffectiveQuantity
 * @since 1.0.0
 */
export function getConfigurationEffectiveQuantity(storedQuantity, quantityCalculation, deal) {
    const dayCount = deal ? getPlannedDateDayCount(deal) : 1;
    return getEffectiveConfigurationQuantity(storedQuantity, quantityCalculation, dayCount);
}
/**
 * @function formatQuantityDayMultiplierHint
 * @since 1.0.0
 */
export function formatQuantityDayMultiplierHint(quantityCalculation, deal) {
    if (!isPerUnitPerDayCalculation(quantityCalculation)) {
        return null;
    }
    const dayCount = deal ? getPlannedDateDayCount(deal) : 1;
    return `× ${dayCount}`;
}
/**
 * @function getConfigurationQuantitySuffix
 * @since 1.0.0
 */
export function getConfigurationQuantitySuffix(quantityCalculation, deal, unitLabel) {
    var _a;
    const dayHint = formatQuantityDayMultiplierHint(quantityCalculation, deal);
    const unit = (_a = unitLabel === null || unitLabel === void 0 ? void 0 : unitLabel.trim()) !== null && _a !== void 0 ? _a : '';
    const parts = [unit, dayHint].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : undefined;
}
/**
 * @function formatEffectiveQuantityHint
 * @since 1.0.0
 */
export function formatEffectiveQuantityHint(storedQuantity, quantityCalculation, deal) {
    const dayMultiplierHint = formatQuantityDayMultiplierHint(quantityCalculation, deal);
    if (!dayMultiplierHint) {
        return null;
    }
    const dayCount = deal ? getPlannedDateDayCount(deal) : 1;
    const effective = storedQuantity * dayCount;
    return `${storedQuantity} ${dayMultiplierHint} = ${effective}`;
}
/**
 * @function getConfigurationLineTotal
 * @since 1.0.0
 */
export function getConfigurationLineTotal(price, storedQuantity, quantityCalculation, deal) {
    return (price *
        getConfigurationEffectiveQuantity(storedQuantity, quantityCalculation, deal));
}
